import React, { Component, createRef, ReactNode, RefObject } from "react";
import "./index.scss";
import { NextIcon, PreviousIcon } from "components/Icon";

interface CarouselProps {
  items: ReactNode[];
  className?: string;
  itemDuration?: number;
  pauseOnInteraction?: boolean;
  showNextButton?: boolean;
  showPreviousButton?: boolean;
  showPagination?: boolean;
  currentItem?: number;
  getItemEnabled?: (element: HTMLElement) => boolean;
}

interface CarouselState {
  lastItemIndex: number;
  currentItemIndex: number;
}

export default class Carousel extends Component<CarouselProps, CarouselState> {
  private static MIN_ITEMS = 5;

  state = {
    lastItemIndex: -1,
    currentItemIndex: 0,
  };

  private _itemTimeout: any = null;
  private _itemRef: RefObject<HTMLUListElement> = createRef();

  public get currentItemIndex(): number {
    return this.state.currentItemIndex % this.props.items.length;
  }
  public set currentItemIndex(value: number) {
    if (value === this.currentItemIndex) {
      return;
    }
    const itemCount = this.props.items.length;
    const index =
      value + itemCount * Math.floor(this.state.currentItemIndex / itemCount);
    this.setState({
      currentItemIndex: index,
      lastItemIndex: this.state.currentItemIndex,
    });
  }

  private get pauseOnInteraction(): boolean {
    return (
      this.props.pauseOnInteraction === undefined ||
      this.props.pauseOnInteraction === true
    );
  }
  private get renderedItemCount(): number {
    let renderedItemCount = this.props.items.length;
    if (renderedItemCount === 0) {
      return 0;
    } else if (renderedItemCount >= Carousel.MIN_ITEMS) {
      return renderedItemCount;
    } else {
      return (
        Math.ceil(Carousel.MIN_ITEMS / renderedItemCount) * renderedItemCount
      );
    }
  }

  componentDidMount(): void {
    this.restartItemTimer(this.props);
    if (
      this.props.currentItem !== undefined &&
      this.props.currentItem !== this.currentItemIndex
    ) {
      this.currentItemIndex = this.props.currentItem;
    }
  }

  componentDidUpdate(
    prevProps: Readonly<CarouselProps>,
    prevState: Readonly<CarouselState>,
    snapshot?: any
  ): void {
    if (
      prevProps.itemDuration !== this.props.itemDuration ||
      prevProps.items !== this.props.items
    ) {
      this.restartItemTimer(this.props);
    }
    if (
      prevProps.currentItem !== this.props.currentItem &&
      this.props.currentItem !== undefined &&
      this.props.currentItem !== this.currentItemIndex
    ) {
      this.currentItemIndex = this.props.currentItem;
    }
  }

  componentWillUnmount(): void {
    if (this._itemTimeout) {
      clearInterval(this._itemTimeout);
      this._itemTimeout = null;
    }
  }

  render() {
    let items = this.props.items;
    while (
      this.props.items.length > 0 &&
      items.length < this.renderedItemCount
    ) {
      items = items.concat(this.props.items);
    }
    const currentIndex = this.state.currentItemIndex;
    const fullClassName = this.props.className
      ? `carousel ${this.props.className}`
      : "carousel";
    return (
      <div
        className={fullClassName}
        onMouseOver={this.onMouseOver}
        onMouseOut={this.onMouseOut}
      >
        {this.props.items && (
          <div className="spacer-item item">{this.props.items[0]}</div>
        )}
        <ul className="items" ref={this._itemRef}>
          {items.map((i, index) => (
            <li
              key={index}
              className={this.getItemClassName(index, currentIndex)}
            >
              {i}
            </li>
          ))}
        </ul>
        {this.props.showPagination && this.renderPagination()}
        {this.props.showPreviousButton && (
          <button className="previous" onClick={this.onPreviousClick}>
            <PreviousIcon />
          </button>
        )}
        {this.props.showNextButton && (
          <button className="next" onClick={this.onNextClick}>
            <NextIcon />
          </button>
        )}
      </div>
    );
  }

  renderPagination = () => {
    const items = this.props.items;
    const currentItemIndex = this.currentItemIndex;
    return (
      <ul className="pagination">
        {items.map((i, index) => (
          <li
            key={index}
            className={index === currentItemIndex ? "current" : ""}
          >
            <button onClick={() => this.onPageClick(index)} />
          </li>
        ))}
      </ul>
    );
  };

  private stopItemTimer() {
    if (this._itemTimeout) {
      clearInterval(this._itemTimeout);
      this._itemTimeout = null;
    }
  }

  private restartItemTimer(props: CarouselProps) {
    this.stopItemTimer();
    if (props.items.length > 0 && props.itemDuration) {
      this._itemTimeout = setInterval(() => this.move(1), props.itemDuration);
    }
  }

  getItemClassName(index: number, currentIndex: number) {
    const itemCount = this.renderedItemCount;
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : itemCount - 1;
    const nextOffset =
      index >= currentIndex
        ? index - currentIndex
        : itemCount + index - currentIndex;
    const prevOffset =
      index <= currentIndex
        ? currentIndex - index
        : itemCount + currentIndex - index;
    let prefix = "item ";
    if (nextOffset <= prevOffset) {
      prefix += `next-${nextOffset} `;
    } else {
      prefix += `previous-${prevOffset} `;
    }
    if (index === currentIndex) {
      return "item current";
    } else if (index === (currentIndex + 1) % itemCount) {
      return prefix + "next";
    } else if (index === prevIndex) {
      return prefix + "previous";
    } else {
      return prefix;
    }
  }

  move = (offset: number) => {
    const list = this._itemRef.current;
    const itemCount = this.renderedItemCount;
    let newIndex = this.state.currentItemIndex;
    let iterationCount = 0;
    do {
      newIndex = (newIndex + offset) % itemCount;
      while (newIndex < 0) {
        newIndex += itemCount;
      }
      iterationCount++;
    } while (
      iterationCount < itemCount &&
      this.props.getItemEnabled &&
      list &&
      !this.props.getItemEnabled(list.children[newIndex] as HTMLElement)
    );
    this.setState({
      currentItemIndex: newIndex,
      lastItemIndex: this.state.currentItemIndex,
    });
  };

  onMouseOver = () => {
    if (this.pauseOnInteraction) {
      this.stopItemTimer();
    }
  };

  onMouseOut = () => {
    if (this.pauseOnInteraction) {
      this.restartItemTimer(this.props);
    }
  };

  onNextClick = () => {
    this.move(1);
  };

  onPreviousClick = () => {
    this.move(-1);
  };

  onPageClick = (index: number) => {
    this.currentItemIndex = index;
  };
}
